import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonMenuButton,
  IonTitle,
  IonContent,
  IonLoading,
} from "@ionic/react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Box,
  Alert,
  AlertTitle,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { requestID, baseUrl, config } from "../../common/utils";
import { formattedDate } from "../../common/utils_t";
import { useBusinessContext } from "../../context/business-context";
import "./InterestPayment.css";

const InterestPayment = () => {
  const { selectedBusiness } = useBusinessContext()
  const { name } = useParams<{ name: string }>();
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [reportData, setReportData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [status, setStatus] = useState({ loading: false, error: false });
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertMsg, setAlertMsg] = useState<string>("");

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const filteredData = reportData?.filter(
    (row) =>
      row.uname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.phone?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };
  
  const onShowAlert = () => {
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const onGetInterestPayment = async () => {
    setStatus({ loading: true, error: false });
    console.log(selectedMonth, selectedYear);
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("year", selectedYear);
      formData.append("business_id", selectedBusiness.business_id);
      formData.append("branch_id", selectedBusiness.branch_id);
      const response = await axios.post(
        baseUrl() + "users/interestpayments_caas",
        formData,
        config
      );
      console.log(response);
      const data = await response.data;
      if (data.status === true) {
        setReportData(data.data);
        setStatus({ loading: false, error: false });
        setAlertMsg(response.data.message);
        onShowAlert();
      } else {
        setAlertMsg(response.data.message);
        setReportData([]);
        onShowAlert();
        setStatus({ loading: false, error: true });
      }
    } catch (error) {
      setStatus({ loading: false, error: false });
      console.error("Error approving loan:", error);
    }
  };
  
  const years = Array.from(new Array(10), (_, index) => currentYear - index);

  return (
    <IonPage className="interest-payment-page pushDown">
      <IonHeader>
        <IonToolbar className="interest-header">
          <IonButtons slot="start">
            <IonMenuButton className="menu-button" />
          </IonButtons>
          <IonTitle>{name}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="interest-content">
        <IonHeader collapse="condense">
          <IonToolbar className="interest-header">
            <IonTitle size="large">{name}</IonTitle>
          </IonToolbar>
        </IonHeader>
        
        <Container component="main" className="interest-container">
          {showAlert && (
            <Alert severity={status.error ? "error" : "success"} className="alert-message">
              <AlertTitle>{status.error ? "Error" : "Success"}</AlertTitle>
              {alertMsg}
            </Alert>
          )}
          
          <Card className="control-card">
            <CardContent>
              <Typography variant="h6" className="card-title">
                Generate Interest Report
              </Typography>
              
              <Grid container spacing={2} alignItems="center">
                {/* Year Dropdown */}
                <Grid item xs={12} sm={6} md={6}>
                  <FormControl fullWidth className="form-control">
                    <InputLabel id="year-label">Select Year</InputLabel>
                    <Select
                      labelId="year-label"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      label="Select Year"
                    >
                      <MenuItem value={""}>Please select a Year</MenuItem>
                      {years.map((year, index) => (
                        <MenuItem key={index} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Generate Report Button */}
                <Grid item xs={12} sm={6} md={6}>
                  <Button
                    variant="contained"
                    className="generate-button"
                    onClick={onGetInterestPayment}
                    fullWidth
                    disabled={!selectedYear}
                  >
                    Generate Report
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {reportData.length > 0 && (
            <Card className="search-card">
              <CardContent>
                <TextField
                  label="Search by Name or Phone"
                  variant="outlined"
                  fullWidth
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="search-field"
                />
              </CardContent>
            </Card>
          )}

          {/* Report Display */}
          {filteredData.length > 0 ? (
            <Card className="report-card">
              <CardContent>
                <Typography variant="h6" className="report-title">
                  Interest Payment Report
                </Typography>
                
                <TableContainer className="table-container">
                  <Table className="interest-table">
                    <TableHead>
                      <TableRow>
                        <TableCell className="table-header">ID</TableCell>
                        <TableCell className="table-header">Name</TableCell>
                        <TableCell className="table-header">Email</TableCell>
                        <TableCell className="table-header">Phone</TableCell>
                        <TableCell className="table-header">Saving Amount</TableCell>
                        <TableCell className="table-header">Saving Balance</TableCell>
                        <TableCell className="table-header">Joined Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((row, i) => (
                          <TableRow key={i} className="table-row">
                            <TableCell className="table-cell">{i + 1}</TableCell>
                            <TableCell className="table-cell">{row?.uname}</TableCell>
                            <TableCell className="table-cell">{row?.email}</TableCell>
                            <TableCell className="table-cell">{row?.phone}</TableCell>
                            <TableCell className="table-cell">{row?.savingAmount}</TableCell>
                            <TableCell className="table-cell">{row?.savingBal}</TableCell>
                            <TableCell className="table-cell">{formattedDate(row?.dateCreated)}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={reportData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  className="pagination"
                />
              </CardContent>
            </Card>
          ) : (
            <Card className="empty-state-card">
              <CardContent className="empty-state-content">
                <Box className="empty-state-icon">
                  <i className="fas fa-coins"></i>
                </Box>
                <Typography variant="h6" className="empty-state-text">
                  No data available. Generate a report to view interest payments.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Container>
        
        <IonLoading
          message="Please wait..."
          spinner="circles"
          isOpen={status.loading}
        />
      </IonContent>
    </IonPage>
  );
};

export default InterestPayment;